import type { RuntimeConfig } from 'nuxt/schema';

import { useConfigStore } from '~/stores/config';
import { loadMetaPixel } from '~/utils/analytics/loadMetaPixel';

/**
 * E2E hook for the page-scoped schools Meta Pixel. Set on the teachers landing
 * and `/educators/signup` only — keep in sync with
 * `playwright/tests/schools-host/marketingTechnologySuppression.spec.ts`.
 */
export const SCHOOLS_META_PIXEL_HOOK_KEY = '__schoolsMetaPixel';

const pixelConfig = (config: RuntimeConfig) => ({
  id: config.public.scripts.metaPixel?.id,
  analyticsEnabled: Boolean(config.public.analyticsEnabled),
});

const markHook = () => {
  if (typeof window === 'undefined') {
    return;
  }
  const registry = window as unknown as Record<string, boolean | undefined>;
  registry[SCHOOLS_META_PIXEL_HOOK_KEY] = true;
};

const hookAlreadyBound = () => {
  if (typeof window === 'undefined') {
    return false;
  }
  const registry = window as unknown as Record<string, boolean | undefined>;

  return Boolean(registry[SCHOOLS_META_PIXEL_HOOK_KEY]);
};

/**
 * Anyway Meta Pixel on the schools-host teachers landing and teacher signup.
 * Schools clusters keep the Marketing tier suppressed, so this is not registered
 * through `06.analytics.ts`. No-ops on consumer and when analytics/pixel id are off.
 */
export const useSchoolsMetaPixel = () => {
  const isSchools = Boolean(useConfigStore().isSchoolsCluster);
  const { id, analyticsEnabled } = pixelConfig(useRuntimeConfig());
  const alreadyBound = hookAlreadyBound();

  if (isSchools) {
    markHook();
  }

  const pixel = isSchools && analyticsEnabled && id
    ? loadMetaPixel(id)
    : null;

  const ensurePageView = () => {
    if (!isSchools) {
      return;
    }
    if (pixel && alreadyBound) {
      pixel.proxy.fbq('track', 'PageView');
    }
  };

  const trackCompleteRegistration = () => {
    if (!isSchools) {
      return;
    }
    pixel?.proxy.fbq('track', 'CompleteRegistration');
  };

  const trackLead = () => {
    if (!isSchools) {
      return;
    }
    pixel?.proxy.fbq('track', 'Lead');
  };

  return { ensurePageView, trackCompleteRegistration, trackLead };
};
